import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { fetchAccountDetails } from "../../../redux/Slice/Dashboard/dashboardSlice";
import { fetchUserPermissions } from "../../../redux/Slice/User/userDetailsSlice";

const General = () => {
  const dispatch = useDispatch();
  const { account, isLoading } = useSelector((state) => state.dashboard);
  const WorkspacePermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Workspace || {}
  );
  const [open, setOpen] = useState(false); // Modal open/close state

  useEffect(() => {
    dispatch(fetchAccountDetails()); // Fetch account details when the component mounts
    dispatch(fetchUserPermissions()); // Fetch user permissions
  }, [dispatch]);

  // Open the modal
  const handleOpen = () => {
    setOpen(true);
  };

  // Close the modal
  const handleClose = () => {
    setOpen(false);
  };

  // Handle the delete action by email or WhatsApp
  const handleDeleteAction = (action) => {
    if (action === "email") {
      window.location.href = "mailto:clusterden@gmail.com"; // Open email client
    } else if (action === "whatsapp") {
      window.open("https://wa.me/clusterden", "_blank"); // Open WhatsApp
    }
    setOpen(false);
  };

  // Check permissions with debugging logs
  const canDeleteWorkspace = WorkspacePermissions.Delete || false;
  const canViewWorkspace = WorkspacePermissions.View || false;

  // If user doesn't have view permission, return null
  if (!canViewWorkspace && !isLoading) {
    return null;
  }

  // console.log("Rendering main component view");
  return (
    <Box
      sx={{
        padding: "20px",
        height: "100%",
        borderRadius: "8px",
        backgroundColor: "background.paper",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        display: "flex",
        flexDirection: "column",
        color: "text.primary",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: "4px",
          // Hide scrollbar but keep scrolling functionality for xs and sm, decrease size for md/lg/xl
          scrollbarWidth: { xs: "none", sm: "none", md: "thin", lg: "thin", xl: "thin" }, // Firefox
          scrollbarColor: (theme) =>
            theme.palette.mode === "dark" ? "#555 #1a1a1a" : "#888 #f1f1f1",
          msOverflowStyle: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" }, // IE and Edge (old)
          "&::-webkit-scrollbar": {
            width: { xs: 0, sm: 0, md: "3px", lg: "3px", xl: "3px" }, // Hide scrollbar on xs and sm, very thin for md/lg/xl
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#1a1a1a" : "#f1f1f1",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#555" : "#888",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#777" : "#555",
            },
          },
        }}
      >
      <Typography
        variant="h5"
        sx={{
          marginBottom: { xs: "1rem", sm: "2rem" },
          fontSize: { xs: "1.125rem", sm: "1.375rem", md: "1.5rem" },
          fontWeight: 600,
        }}
      >
        Account
      </Typography>

      {isLoading ? (
        <Typography sx={{ fontSize: "1rem" }}>Loading...</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
          {/* Account Name */}
          <TextField
            label="Account Name"
            value={account?.Name || "Loading..."}
            fullWidth
            disabled
            sx={(theme) => ({
              "& .MuiInputLabel-root": {
                color: "text.secondary",
                fontSize: "1rem",
              },
              "& .MuiInputBase-input": {
                color: "text.primary",
                fontSize: "1rem",
              },
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.87)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "primary.main",
                },
              },
            })}
          />

          <Box sx={{ display: "inline-block" }}>
            <Tooltip
              title={
                !canDeleteWorkspace
                  ? "You don't have permission to delete workspace"
                  : ""
              }
              arrow
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleOpen}
                  disabled={!canDeleteWorkspace}
                  sx={{
                    fontSize: "0.9375rem",
                    width: "fit-content",
                    paddingX: "1.5rem",
                    "&:hover": {
                      backgroundColor: "error.dark",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
                      color: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.38)",
                    },
                  }}
                >
                  Delete Workspace
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Modal for Delete Workspace */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: "background.paper",
            color: "text.primary",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "1.25rem" }}>Delete Workspace</DialogTitle>
        <DialogContent sx={{ "& .MuiTypography-root": { fontSize: "1rem" } }}>
          <Typography>
            Are you sure you want to delete your workspace? To proceed, please
            either:
          </Typography>
          <Typography
            variant="body2"
            sx={{ marginTop: "1rem", color: "text.secondary", fontSize: "0.9375rem" }}
          >
            1. Email us at{" "}
            <strong style={{ color: "text.primary" }}>
              clusterden@gmail.com
            </strong>{" "}
            or
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.9375rem" }}>
            2. Contact via WhatsApp at{" "}
            <strong style={{ color: "text.primary" }}>clusterden</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDeleteAction("email")}
            color="primary"
            sx={{
              fontSize: "0.9375rem",
              "&:hover": {
                backgroundColor: "rgba(14, 144, 70, 0.08)",
              },
            }}
          >
            Email Us
          </Button>
          <Button
            onClick={() => handleDeleteAction("whatsapp")}
            color="primary"
            sx={{
              fontSize: "0.9375rem",
              "&:hover": {
                backgroundColor: "rgba(14, 144, 70, 0.08)",
              },
            }}
          >
            Contact via WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Box>
  );
};

export default General;